import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import { Button, Intent, Icon, Callout, TextArea } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import BaseDialog from "./BaseDialog";
import TextSpan from "../TextSpan";
import { IEsvcsEnrollment } from "csd.phoenix.models/EsvcsEnrollment";
import { EsvcsEnrollmentStatuses } from "csd.phoenix.models/EsvcsEnrollmentStatuses";
import { 
  checkLastV2OrV2SenseiRelatedEnrollmentStatus, 
  deactivateEnrollmentComprehensive 
} from "../../../stores/enrollmentsStore";

interface ISingleButtonActions {
  buttonLabel: string;
  buttonIntent: Intent;
  onClick: () => void;
}

const SingleButtonActions: React.FC<ISingleButtonActions> = memo(({
  buttonLabel,
  buttonIntent,
  onClick
}) => {
  return (
    <div className="centered">
      <Button
        text={buttonLabel}
        intent={buttonIntent}
        onClick={onClick}
        style={{ minWidth: 120, margin: '0 1em' }}
      />
    </div>
  );
});

interface IDeactivateActions {
  okButtonLabel?: string;
  okButtonIntent?: Intent;
  onOk: () => void;
  onCancel: () => void;
  disableOkButton?: boolean;
  disableCancelButton?: boolean;
}

const DeactivateActions: React.FC<IDeactivateActions> = memo(({ 
  okButtonLabel, 
  okButtonIntent = Intent.DANGER, 
  onOk, 
  onCancel, 
  disableOkButton = false,
  disableCancelButton = false
}) => {
  return (
    <div className="centered">
      <Button
        text={okButtonLabel}
        intent={okButtonIntent}
        onClick={onOk}
        style={{ minWidth: 120, margin: '0 1em' }}
        disabled={disableOkButton}
      />
      <Button
        text="Cancel"
        intent={Intent.WARNING}
        onClick={onCancel}
        style={{ minWidth: 120, margin: '0 1em' }}
        disabled={disableCancelButton}
      />
    </div>
  );
});

enum DeactivateStep {
  InitialConfirm,
  TenantDeletionCustomerDeactivationConfirm,
  NoteEntry,
  Processing,
  Success,
  Error
}

interface IDialogContentProps {
  step: DeactivateStep;
  enrollment: IEsvcsEnrollment;
  senseiMessage: string;
  hasActiveTenant: boolean;
  note: string;
  setNote: (note: string) => void;
  isPartialSuccess: boolean;
  hasWarnings: boolean;
  resultMessage: string;
  resultSteps: string[];
  resultNotes: string;
  errorMessage: string;
}

const DialogContent: React.FC<IDialogContentProps> = memo(({
  step,
  enrollment,
  senseiMessage,
  hasActiveTenant,
  note,
  setNote,
  isPartialSuccess,
  hasWarnings,
  resultMessage,
  resultSteps,
  resultNotes,
  errorMessage
}) => {
  switch (step) {
    case DeactivateStep.InitialConfirm:
      return (
        <div style={{ textAlign: 'center' }}>
          Are you sure you want to deactivate this enrollment for <strong>{enrollment.CustomerName}</strong>?
        </div>
      );
    
    case DeactivateStep.TenantDeletionCustomerDeactivationConfirm:
      return (
        <div style={{ textAlign: 'center' }}>
          {senseiMessage && (
            <Callout intent={Intent.WARNING} style={{ marginBottom: '1em', fontWeight: 'bold', textAlign: 'left' }}>
              <div style={{ fontWeight: 'bold' }}>
                {senseiMessage.split('. ')[0] + (senseiMessage.includes('. ') ? '.' : '')}
              </div>
              {senseiMessage.includes('. ') && (
                <div style={{ marginTop: '0.5em', fontWeight: 'bold' }}>
                  {senseiMessage.substring(senseiMessage.indexOf('. ') + 2)}
                </div>
              )}
            </Callout>
          )}
          {hasActiveTenant && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '1em', fontWeight: 'bold', textAlign: 'left' }}>
              <div style={{ fontWeight: 'bold' }}>
                This customer has an associated Sensei SQL database.
              </div>
              <div style={{ marginTop: '0.5em', fontWeight: 'bold' }}>
                If you choose to proceed, the tenant's related Sensei SQL database will be deleted.
              </div>
            </Callout>
          )}
          <div style={{ marginTop: '1em' }}>
            Do you want to proceed?
          </div>
        </div>
      );
    
    case DeactivateStep.NoteEntry:
      return (
        <div>
          <div style={{ marginBottom: '1em', textAlign: 'center' }}>
            <strong>Please enter a note explaining the reason for deactivation:</strong>
          </div>
          <TextArea
            fill
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter deactivation reason..."
            rows={4}
            style={{ minHeight: '100px' }}
          />
          {(!note || note.trim().length === 0) && (
            <div style={{ marginTop: '0.5em', color: '#DB3737', fontSize: '0.875em' }}>
              Note is required to proceed with deactivation.
            </div>
          )}
        </div>
      );
    
    case DeactivateStep.Processing:
      return (
        <div style={{ textAlign: 'center' }}>
          Processing deactivation...
        </div>
      );
    
    case DeactivateStep.Success:
      return (
        <div>
          <TextSpan 
            intent={isPartialSuccess ? Intent.DANGER : (hasWarnings ? Intent.WARNING : Intent.SUCCESS)} 
            style={{ display: 'block', marginBottom: '1em', textAlign: 'center', fontWeight: 'bold', color: 'black' }}
          >
            {resultMessage || (isPartialSuccess ? "Enrollment deactivation encountered problems!" : (hasWarnings ? "Enrollment deactivated with warnings!" : "Enrollment deactivated successfully!"))}
          </TextSpan>
          {resultSteps.length > 0 && (
            <Callout intent={isPartialSuccess ? Intent.DANGER : Intent.SUCCESS} style={{ marginBottom: '1em', textAlign: 'left' }} title={isPartialSuccess ? "Attempted steps" : "Completed steps"}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {resultSteps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ul>
            </Callout>
          )}
          {resultNotes && resultNotes.trim().length > 0 && (
            <Callout intent={Intent.PRIMARY} style={{ marginBottom: '1em', textAlign: 'left' }} title="Notes">
              {resultNotes}
            </Callout>
          )}
        </div>
      );
    
    case DeactivateStep.Error:
      return (
        <div style={{ textAlign: 'center' }}>
          <TextSpan intent={Intent.DANGER}>{errorMessage}</TextSpan>
        </div>
      );
    
    default:
      return null;
  }
});

interface IDeactivateDialogProps {
  title?: string;
  isOpen: boolean;
  enrollment: IEsvcsEnrollment;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeactivateDialog: React.FC<IDeactivateDialogProps> = ({ 
  title = "Deactivate Enrollment", 
  isOpen, 
  onClose, 
  enrollment,
  onSuccess 
}) => {
  const [step, setStep] = useState<DeactivateStep>(DeactivateStep.InitialConfirm);
  const [disableButtons, setDisableButtons] = useState(false);
  const [senseiMessage, setSenseiMessage] = useState<string>("");
  const [resultSteps, setResultSteps] = useState<string[]>([]);
  const [resultMessage, setResultMessage] = useState<string>("");
  const [hasWarnings, setHasWarnings] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasActiveTenant, setHasActiveTenant] = useState<boolean>(false);
  const [resultNotes, setResultNotes] = useState<string>("");
  const [isPartialSuccess, setIsPartialSuccess] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const [deactivateParams, setDeactivateParams] = useState<{ deleteTenant: boolean; deactivateCustomer: boolean }>({ 
    deleteTenant: false, 
    deactivateCustomer: false 
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep(DeactivateStep.InitialConfirm);
      setDisableButtons(false);
      setSenseiMessage("");
      setResultSteps([]);
      setResultMessage("");
      setHasWarnings(false);
      setErrorMessage("");
      setHasActiveTenant(false);
      setResultNotes("");
      setIsPartialSuccess(false);
      setNote("");
      setDeactivateParams({ deleteTenant: false, deactivateCustomer: false });
    }
  }, [isOpen]);

  const performDeactivation = useCallback(
    async (deleteTenantInstallation: boolean, deactivateCustomerAndAgent: boolean = false) => {
      if (!enrollment.RecordId) return;
      setDisableButtons(true);
      setStep(DeactivateStep.Processing);
      try {
        const result = await deactivateEnrollmentComprehensive(
          enrollment.RecordId,
          note, // Use the note from state
          deactivateCustomerAndAgent,
          deleteTenantInstallation
        );
      
      // Debug logging
      // console.log("Deactivation result:", result);
      // console.log("RecordStatus:", result?.RecordStatus);
      // console.log("RecordId:", result?.RecordId);
      // console.log("Message:", result?.Message);
      
      // Check for successful response - backend returns RecordStatus for successful operations
      if (result && result.RecordStatus !== undefined) {
        const steps = result.Steps || [];
        setResultSteps(steps);
        setResultMessage(result.Message || "");
        setHasWarnings(result.HasWarnings || false);
        setResultNotes(result.Notes || "");
        // Check if this is a partial success (has warnings with errors, or steps contain error messages)
        const hasErrorsInSteps = steps.some(step => step.toUpperCase().includes("ERROR:") || step.toUpperCase().includes("FAILED"));
        setIsPartialSuccess(result.HasWarnings && hasErrorsInSteps);
        setStep(DeactivateStep.Success);
        // No toast; show result in dialog only
        // onSuccess will be called after user clicks OK
      } else {
        console.error("Unexpected result format:", result);
        setErrorMessage(result?.Message || "Failed to deactivate enrollment. Please try again.");
        setStep(DeactivateStep.Error);
      }
    } catch (error) {
      console.error("Deactivation error:", error);
      setErrorMessage("An error occurred while deactivating the enrollment. Please try again.");
      setStep(DeactivateStep.Error);
    } finally {
      setDisableButtons(false);
    }
  }, [enrollment.RecordId, note]);

  const handleInitialConfirm = useCallback(async () => {
    if (!enrollment.RecordId) {
      console.error("Cannot check last enrollment status: enrollment.RecordId is missing or null");
      setErrorMessage("Enrollment ID is missing. Cannot check status.");
      setStep(DeactivateStep.Error);
      return;
    }

    // For New enrollments, skip the last enrollment check and go to note entry
    if (enrollment.EnrollmentStatus === EsvcsEnrollmentStatuses.New) {
      setDeactivateParams({ deleteTenant: false, deactivateCustomer: false });
      setStep(DeactivateStep.NoteEntry);
      return;
    }

    setDisableButtons(true);
    try {
      //console.log("Calling checkLastV2OrV2SenseiRelatedEnrollmentStatus with RecordId:", enrollment.RecordId);
      // Check what actions are needed (backend determines this)
      const checkResult = await checkLastV2OrV2SenseiRelatedEnrollmentStatus(enrollment.RecordId);

      // Store the backend's decision about what to do
      const shouldDeactivateCustomer = checkResult.ShouldDeactivateCustomerAndAgent || false;
      const shouldDeleteTenant = checkResult.ShouldDeleteTenantInstallation || false;
      const hasActiveTenant = !!checkResult.HasActiveTenant;

      setDeactivateParams({ 
        deleteTenant: shouldDeleteTenant, 
        deactivateCustomer: shouldDeactivateCustomer 
      });

      if (shouldDeactivateCustomer || shouldDeleteTenant) {
        // Show confirmation dialog with message and warnings
        setSenseiMessage(checkResult.Message || "");
        setHasActiveTenant(hasActiveTenant);
        setStep(DeactivateStep.TenantDeletionCustomerDeactivationConfirm);
        setDisableButtons(false);
      } else {
        // No special actions needed - proceed directly to note entry
        setStep(DeactivateStep.NoteEntry);
        setDisableButtons(false);
      }
    } catch (error) {
      console.error("Failed to check enrollment status:", error);
      setErrorMessage("Failed to check enrollment status. Please try again.");
      setStep(DeactivateStep.Error);
      setDisableButtons(false);
    }
  }, [enrollment.EnrollmentStatus, enrollment.RecordId]);

  const handleTenantDeletionCustomerDeactivationConfirm = useCallback(async () => {
    // Params already set in handleInitialConfirm, just proceed to note entry
    setStep(DeactivateStep.NoteEntry);
  }, []);

  const handleNoteConfirm = useCallback(async () => {
    // Require note to be entered
    if (!note || note.trim().length === 0) {
      return;
    }
    await performDeactivation(deactivateParams.deleteTenant, deactivateParams.deactivateCustomer);
  }, [note, deactivateParams, performDeactivation]);

  const handleOk = useCallback(() => {
    if (step === DeactivateStep.InitialConfirm) {
      handleInitialConfirm();
    } else if (step === DeactivateStep.TenantDeletionCustomerDeactivationConfirm) {
      handleTenantDeletionCustomerDeactivationConfirm();
    } else if (step === DeactivateStep.NoteEntry) {
      handleNoteConfirm();
    } else if (step === DeactivateStep.Success) {
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } else if (step === DeactivateStep.Error) {
      onClose();
    }
  }, [step, handleInitialConfirm, handleTenantDeletionCustomerDeactivationConfirm, handleNoteConfirm, onClose, onSuccess]);

  const actions = useMemo(() => {
    if (step === DeactivateStep.Success) {
      return (
        <SingleButtonActions
          buttonLabel="OK"
          buttonIntent={Intent.PRIMARY}
          onClick={handleOk}
        />
      );
    }
    if (step === DeactivateStep.Error) {
      return (
        <SingleButtonActions
          buttonLabel="OK"
          buttonIntent={Intent.PRIMARY}
          onClick={onClose}
        />
      );
    }
    if (step === DeactivateStep.Processing) {
      return null;
    }
    // For NoteEntry step, disable OK button if note is empty
    const isOkDisabled = step === DeactivateStep.NoteEntry ? !note || note.trim().length === 0 : disableButtons;
    return (
      <DeactivateActions 
        onOk={handleOk} 
        onCancel={onClose} 
        okButtonLabel="OK" 
        disableOkButton={isOkDisabled}
        disableCancelButton={disableButtons}
      />
    );
  }, [step, handleOk, onClose, note, disableButtons]);

  return (
    <BaseDialog
      title={title}
      isOpen={isOpen}
      onClose={step === DeactivateStep.Processing ? undefined : onClose}
      icon={<Icon icon={IconNames.TRASH} intent={Intent.DANGER} />}
      canEscapeKeyClose={step !== DeactivateStep.Processing}
      isCloseButtonShown={step !== DeactivateStep.Processing}
      canOutsideClickClose={false}
      actions={actions}
    >
      <DialogContent
        step={step}
        enrollment={enrollment}
        senseiMessage={senseiMessage}
        hasActiveTenant={hasActiveTenant}
        note={note}
        setNote={setNote}
        isPartialSuccess={isPartialSuccess}
        hasWarnings={hasWarnings}
        resultMessage={resultMessage}
        resultSteps={resultSteps}
        resultNotes={resultNotes}
        errorMessage={errorMessage}
      />
    </BaseDialog>
  );
};
